import * as _ from 'lodash';
import type { QueryKey } from '@tanstack/react-query';

import { useQuery } from '../../../ReactQuery';
import { get } from '../../../redux/api';
import utils from '../../../utils';
import type { Revision } from '../../../types';

/**
 * The maximum number of revisions to fetch.
 * This is the value that we've always used for the revision dropdown so we'll use it here as well.
 */
const maxRevisionCount = 12;

type GetRevisionHistoryParams = {
  revisionLocator: string;
  revisionLocatorFilter?: string;
  count?: number;
  source: string | null;
  shouldFetchOnlyResolvedRevisions?: boolean;
  shouldFetchOnlyRevisionsWithSnippetAnalysis?: boolean;
};

const getRevisionHistory = ({
  revisionLocator,
  revisionLocatorFilter,
  count = maxRevisionCount,
  source,
  shouldFetchOnlyResolvedRevisions,
  shouldFetchOnlyRevisionsWithSnippetAnalysis,
}: GetRevisionHistoryParams) => {
  return get<{revisions: Revision[]}>(`/api/revisions/${utils.nuc(revisionLocator)}/history`, false, {
    locator: revisionLocatorFilter,
    count,
    offset: 0,
    source,
    resolved: shouldFetchOnlyResolvedRevisions ? true : undefined,
    hasSnippetAnalysis: shouldFetchOnlyRevisionsWithSnippetAnalysis ? true : undefined,
  });
};

const getQueryKey = (params: GetRevisionHistoryParams): QueryKey => ['revision', params.revisionLocator, 'history', _.omit(params, 'revisionLocator')];

/**
 * useRevisionHistory is a hook that fetches the revision history for a revision.
 */
const useRevisionHistory = (params: GetRevisionHistoryParams) => {
  return useQuery({
    queryKey: getQueryKey(params),
    queryFn: () => getRevisionHistory(params),
  });
};

export default useRevisionHistory;
